import type { DayOfWeek } from "./types/foyer";
import type {
  SignageAssignment,
  SignageBreak,
  SignageCampaign,
  SignageContentInterval,
  SignageContentSource,
  SignageDayPlan,
  SignageDayPlanBlock,
  SignageItem,
  SignagePlaylist,
  SignageTimeline,
  ResolvedContentSource,
  SignageWarning,
} from "./types/signage";
import type { ResolvedSignageTemplate } from "./types/signage-template";
import { safeTimeZone } from "./timezone";

/**
 * The signage schedule resolver.
 *
 * Pure and side-effect free so it can run identically on the server (to answer
 * a player's poll), in the CMS (to draw the "what plays today" timeline without
 * a round trip) and in tests. Nothing here touches Mongo, the clock, or `Date.now`
 * - `now` is always passed in.
 *
 * Timezone handling is real, not the portal's wall-clock-stamped-as-Zulu trick:
 * plan blocks and campaign windows are expressed in premise-local `HH:mm`, and
 * this module converts them to true UTC instants using the IANA zone. DST is
 * handled by construction (see {@link zonedTimeToUtc}).
 */

const DAY_KEYS: DayOfWeek[] = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
/**
 * How far BEFORE the first interval a player may be and still resolve it.
 *
 * The server builds a timeline from ITS clock, starting at its own `now`; the
 * player matches those intervals against ITS OWN. That is asymmetric in the
 * worst direction: a panel whose clock trails the server's by even a minute
 * falls before every interval and resolves nothing - a black wall reporting
 * "Nothing scheduled right now", indistinguishable from a screen somebody
 * forgot to schedule. A panel AHEAD is covered for hours.
 *
 * A signage panel is exactly the device this happens to: no RTC battery, no NTP
 * guarantee, and it reboots.
 *
 * The tolerance lives HERE, in the player, and deliberately not in the resolver.
 * Back-dating the resolved intervals was the first attempt and the tests caught
 * its blast radius: `content[0].startsAt` also feeds the LEGACY signage
 * protocol, whose keys the old autoplayer matches by exact string equality
 * against its own wall clock. Shifting them would have broken production
 * devices silently - which is precisely the failure that protocol's own test
 * exists to prevent. Skew belongs where skew happens.
 *
 * It does NOT hide a badly wrong clock: hours or days of drift still resolve to
 * nothing, and must, because that is a real fault and has to stay visible.
 */
const CLOCK_SKEW_GRACE_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/** Minutes since local midnight for `HH:mm`. `24:00` is 1440, not 0. */
export function minutesOfDay(hhmm: string): number {
  const [h, m] = hhmm.split(":");
  const hours = Number(h);
  const mins = Number(m);
  if (!Number.isFinite(hours) || !Number.isFinite(mins)) return 0;
  return hours * 60 + mins;
}

/**
 * The offset (ms) that `tz` is ahead of UTC at `instant`.
 *
 * Derived by formatting the instant in the zone and reading the wall-clock back
 * as if it were UTC. That is the only way to get a zone offset from `Intl`
 * without shipping a timezone database.
 */
function zoneOffsetMs(tz: string, instant: Date): number {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const p = Object.fromEntries(
    fmt.formatToParts(instant).map((x) => [x.type, x.value])
  ) as Record<string, string>;
  // en-US renders midnight as "24" in some ICU versions.
  const hour = p.hour === "24" ? 0 : Number(p.hour);
  const asIfUtc = Date.UTC(
    Number(p.year),
    Number(p.month) - 1,
    Number(p.day),
    hour,
    Number(p.minute),
    Number(p.second)
  );
  return asIfUtc - instant.getTime();
}

/**
 * The true UTC instant of a local wall-clock time in `tz`.
 *
 * Two passes: guess by treating the wall clock as UTC and subtracting the
 * offset there, then re-read the offset at that guess and correct if it moved.
 * The second pass is what makes DST boundaries land correctly - on a spring
 * forward the first guess sits in the skipped hour and reports the pre-jump
 * offset.
 */
export function zonedTimeToUtc(tz: string, ymd: string, minutes: number): Date {
  const [y, m, d] = ymd.split("-").map(Number);
  const naive =
    Date.UTC(y!, (m ?? 1) - 1, d ?? 1, 0, 0, 0) + minutes * MINUTE_MS;
  const first = zoneOffsetMs(tz, new Date(naive));
  let ts = naive - first;
  const second = zoneOffsetMs(tz, new Date(ts));
  if (second !== first) ts = naive - second;
  return new Date(ts);
}

/** `YYYY-MM-DD` and weekday of `instant` as seen in `tz`. */
export function localDayInZone(
  tz: string,
  instant: Date
): { ymd: string; day: DayOfWeek } {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
  });
  const p = Object.fromEntries(
    fmt.formatToParts(instant).map((x) => [x.type, x.value])
  ) as Record<string, string>;
  const map: Record<string, DayOfWeek> = {
    Sun: "sun",
    Mon: "mon",
    Tue: "tue",
    Wed: "wed",
    Thu: "thu",
    Fri: "fri",
    Sat: "sat",
  };
  return {
    ymd: `${p.year}-${p.month}-${p.day}`,
    day: map[p.weekday!] ?? "mon",
  };
}

/* -------------------------------------------------------------------------- */
/* Interval algebra                                                            */
/* -------------------------------------------------------------------------- */

interface Span {
  start: number;
  end: number;
}

interface SourcedSpan extends Span {
  source: SignageContentSource;
  playlistId: string;
  /**
   * Where this span's loop TRULY began, unclamped by the resolve window -
   * see `SignageContentInterval.loopStartsAt`. `subtract()` only ever touches
   * `start`/`end`, so this rides unchanged through every cut a span survives:
   * a plan block split around an override keeps the SAME loop origin on both
   * remaining pieces, which is what makes resuming after the override land
   * exactly where the block's own clock says it should, not restart fresh.
   */
  loopStart: number;
}

/** `spans` with every part that overlaps any of `cuts` removed. */
function subtract(spans: Span[], cuts: Span[]): Span[] {
  let out = spans;
  for (const cut of cuts) {
    const next: Span[] = [];
    for (const s of out) {
      if (cut.end <= s.start || cut.start >= s.end) {
        next.push(s);
        continue;
      }
      if (cut.start > s.start) next.push({ start: s.start, end: cut.start });
      if (cut.end < s.end) next.push({ start: cut.end, end: s.end });
    }
    out = next;
  }
  return out;
}

/**
 * Layer content sources by precedence: override beats plan beats default.
 *
 * Each lower layer is cut back by every higher one, so the result is a
 * non-overlapping cover. A gap is legitimate and means "show nothing" - the
 * portal cannot express that, because its lowest layer always spans the whole
 * window.
 */
function layerByPrecedence(
  override: SourcedSpan[],
  plan: SourcedSpan[],
  fallback: SourcedSpan[]
): SourcedSpan[] {
  const cut = (spans: SourcedSpan[], cuts: Span[]): SourcedSpan[] =>
    spans.flatMap((s) =>
      subtract([{ start: s.start, end: s.end }], cuts).map((piece) => ({
        ...s,
        ...piece,
      }))
    );

  const planCut = cut(plan, override);
  const fallbackCut = cut(fallback, [...override, ...planCut]);
  return [...override, ...planCut, ...fallbackCut].sort(
    (a, b) => a.start - b.start
  );
}

/* -------------------------------------------------------------------------- */
/* Campaign eligibility                                                        */
/* -------------------------------------------------------------------------- */

/** Does `campaign` cover the local moment given by `ymd` / `minutes`? */
export function campaignCovers(
  campaign: SignageCampaign,
  ymd: string,
  day: DayOfWeek,
  minutes: number
): boolean {
  if (!campaign.enabled) return false;
  if (campaign.items.length === 0) return false;
  if (campaign.startDate && ymd < campaign.startDate) return false;
  if (campaign.endDate && ymd > campaign.endDate) return false;
  if (campaign.days.length > 0 && !campaign.days.includes(day)) return false;

  const start = minutesOfDay(campaign.startTime);
  const end = minutesOfDay(campaign.endTime);
  // Equal bounds mean "all day" - the operator drew no window.
  if (start === end) return true;
  // A window whose end precedes its start wraps past midnight.
  if (start < end) return minutes >= start && minutes < end;
  return minutes >= start || minutes < end;
}

/**
 * Build the rotation order for one break.
 *
 * A `high` campaign is entered twice so it recurs about twice as often without
 * starving `normal` ones. The portal only applies that doubling when BOTH
 * priorities are present, which makes a set of all-high campaigns behave
 * identically to all-normal - harmless, but it means the flag silently does
 * nothing in the most common case. Doubling unconditionally keeps the meaning
 * of the flag stable regardless of what else is booked.
 */
function rotationOrder(campaigns: SignageCampaign[]): SignageCampaign[] {
  const out: SignageCampaign[] = [];
  for (const c of campaigns) {
    out.push(c);
    if (c.priority === "high") out.push(c);
  }
  return out;
}

/* -------------------------------------------------------------------------- */
/* Resolver                                                                    */
/* -------------------------------------------------------------------------- */

export interface ResolveSignageInput {
  assignment: SignageAssignment;
  dayPlans: SignageDayPlan[];
  playlists: SignagePlaylist[];
  campaigns: SignageCampaign[];
  /** IANA zone of the premise. Falls back to UTC rather than to a guess. */
  timezone: string;
  now: Date;
  horizonHours: number;
  /**
   * `contentAssetId → playable URL`, for catalogue titles in a loop
   * (`CONTENT-13`).
   *
   * Resolved by the CALLER, because this function is pure and a catalogue read
   * is an upstream call. The resolution happens at BUILD time and is never
   * stored: an absolutised URL drifts the moment the title is re-packaged, and
   * the drift is invisible until a wall goes black.
   *
   * A title that is absent from this map, or maps to `null`, is DROPPED from
   * the loop with a warning — see `content_unavailable`. Failing the whole
   * timeline instead would take a lobby dark over one re-packaged film.
   */
  contentUrls?: Record<string, ResolvedContentSource | null>;
  /**
   * `templateInstanceId → the slide, flattened with its design`.
   *
   * Resolved by the CALLER for the same reason as `contentUrls`: this function
   * is pure and reading a template is a database call. What it hands back is
   * already the copy that goes on the wire - see `ResolvedSignageTemplate` for
   * why the design is COPIED rather than referenced.
   *
   * An instance absent from this map is DROPPED from the loop with a warning,
   * exactly like an unresolvable catalogue title: a slide whose template was
   * deleted must cost that slide, never the whole wall.
   */
  templates?: Record<string, ResolvedSignageTemplate | null>;
}

/* -- live-channel blocks (BE-41) ------------------------------------------ */

/**
 * Prefix for the playlist a live-channel block is rendered as.
 *
 * `:` cannot occur in a Mongo ObjectId string, so a synthesised id can never
 * collide with a stored playlist's - which matters, because the emitted
 * timeline puts both in one `playlists` array keyed by id.
 */
export const CHANNEL_PLAYLIST_PREFIX = "channel:";

/** The synthesised playlist id for a live channel. */
export function channelPlaylistId(contentAssetId: string): string {
  return `${CHANNEL_PLAYLIST_PREFIX}${contentAssetId}`;
}

/** True when this playlist id names a live channel rather than a stored loop. */
/**
 * The picture that stands for a channel, or null.
 *
 * Its own poster when it has one, and otherwise the first item ONLY IF that
 * item is an image.
 *
 * ⚠️ The `kind` check is the whole point. The fallback used to be
 * `items[0].assetUrl` outright, which is a picture exactly when the loop opens
 * on a still and a broken-image icon every other time: an uploaded loop opens
 * on an MP4, and a catalogue channel opens on a DASH MANIFEST once the
 * timeline has materialised it. Chrome and Safari both draw the same little
 * torn page for `<img src="…/manifest.mpd">`.
 *
 * Stated once because three surfaces draw it - the Channels catalogue, the
 * change-channel picker and the timeline block - and a fallback rule with
 * three homes is one that will disagree with itself.
 *
 * `||`, not `??`: an item with no file url holds an EMPTY STRING, and an empty
 * string is not a poster - it is a request the browser makes for nothing.
 */
export function channelPosterUrl(playlist: {
  posterUrl?: string;
  items: { assetUrl?: string; kind?: string }[];
}): string | null {
  if (playlist.posterUrl) return playlist.posterUrl;
  const first = playlist.items[0];
  if (first?.kind === "image" && first.assetUrl) return first.assetUrl;
  return null;
}

export function isChannelPlaylistId(id: string): boolean {
  return id.startsWith(CHANNEL_PLAYLIST_PREFIX);
}

/**
 * Which playlist a day-plan block resolves to - stored or synthesised.
 *
 * `null` for a block that names neither, which the server refuses at write
 * time; the resolver still tolerates one rather than throwing, because a
 * malformed row must not take a lobby dark.
 */
export function playlistIdOfBlock(block: SignageDayPlanBlock): string | null {
  if (block.playlistId) return block.playlistId;
  if (block.contentAssetId) return channelPlaylistId(block.contentAssetId);
  return null;
}

/**
 * The one-item playlist a live-channel block plays as.
 *
 * Built at resolve time and never stored - see `SignageDayPlanBlock`. Three
 * values in here are load-bearing:
 *
 *  - **one item**, so `playheadAt`'s modulo returns it forever and the channel
 *    runs for the whole block without `durationSeconds` entering the arithmetic;
 *  - **`defaultItemSeconds: 3600`**, which nothing in Foyer reads (one item
 *    never advances) but which a LEGACY autoplayer does: the facade renders
 *    `max(1, itemSeconds(...))`, so leaving it at 0 would tell a fielded player
 *    to re-cue the channel every second;
 *  - **`fit: "contain"`**, because a broadcast channel has a shape nobody chose
 *    and cropping a 16:9 feed onto a portrait totem loses half the picture.
 *
 * `muted` is false: whether the wall makes noise is the SCREEN's setting
 * (`assignment.soundEnabled`), and a channel scheduled by an operator who then
 * turned sound on should be audible. The pub wants the hockey commentary.
 */
export function channelBlockPlaylist(
  block: SignageDayPlanBlock & { contentAssetId: string },
  updatedAt: string
): SignagePlaylist {
  const assetId = block.contentAssetId;
  return {
    id: channelPlaylistId(assetId),
    name: block.channelName?.trim() || "Live channel",
    // Global: a channel belongs to the catalogue, not to whoever scheduled it.
    scope: "global",
    premiseId: null,
    companyId: null,
    items: [
      {
        id: `${CHANNEL_PLAYLIST_PREFIX}${assetId}`,
        // Filled in by `materialiseContent` from the catalogue, exactly as a
        // catalogue item in a stored loop is.
        assetUrl: "",
        contentAssetId: assetId,
        kind: "video",
        durationSeconds: 0,
        fit: "contain",
        muted: false,
      },
    ],
    defaultItemSeconds: 3600,
    soundEnabled: true,
    updatedAt,
  };
}

/**
 * Resolve a screen's timeline over `[now, now + horizonHours)`.
 *
 * The horizon is a window, not a day: a player polling at 23:50 must be able to
 * see past midnight, which is why plan blocks are expanded per local day across
 * the whole window rather than for "today".
 */
export function resolveSignageTimeline(
  input: ResolveSignageInput
): SignageTimeline {
  const { assignment, timezone, now, horizonHours } = input;
  // A stored zone Intl will not accept must NOT throw out of here: this
  // function answers the panel's own poll, and a 500 leaves the screen with no
  // timeline at all - i.e. black - over a typo in a text field. Degrade to UTC
  // and carry the reason out in `warnings` so a human is told which it is.
  const { timeZone: tz, invalid: badZone } = safeTimeZone(timezone);
  const warnings: SignageWarning[] = [];
  if (badZone) {
    warnings.push({
      code: "invalid_timezone",
      value: badZone,
      message: `"${badZone}" is not a timezone this system recognises, so times were resolved in UTC. Set the location's timezone to an IANA name such as "Europe/Stockholm".`,
    });
  }
  const from = now.getTime();
  const to = from + horizonHours * HOUR_MS;

  const playlistById = new Map(input.playlists.map((p) => [p.id, p]));
  // A live-channel block carries no playlist, so one is SYNTHESISED here and
  // dropped into the same map. Everything downstream - precedence, the emitted
  // intervals, `materialiseContent`, the legacy facade, `playheadAt` - then
  // works on a channel block without knowing it is one. See `BE-41`.
  for (const dayPlan of input.dayPlans) {
    for (const block of dayPlan.blocks) {
      const contentAssetId = block.contentAssetId;
      if (!contentAssetId) continue;
      const id = channelPlaylistId(contentAssetId);
      if (!playlistById.has(id)) {
        playlistById.set(
          id,
          channelBlockPlaylist({ ...block, contentAssetId }, dayPlan.updatedAt)
        );
      }
    }
  }
  const dayPlanById = new Map(input.dayPlans.map((p) => [p.id, p]));

  /* -- content layers ---------------------------------------------------- */

  const override: SourcedSpan[] = [];
  if (assignment.override && playlistById.has(assignment.override.playlistId)) {
    const since = Date.parse(assignment.override.since);
    const until = assignment.override.until
      ? Date.parse(assignment.override.until)
      : to;
    const start = Math.max(from, since);
    const end = Math.min(to, until);
    if (end > start) {
      override.push({
        start,
        end,
        source: "override",
        playlistId: assignment.override.playlistId,
        // The takeover's OWN start, not clipped to `from` - a takeover
        // resolved on its 40th poll should anchor exactly like its 1st.
        loopStart: since,
      });
    }
  }

  // Expand the weekly plan across every local day the window touches. One day
  // either side absorbs blocks that begin before `from` and zone offsets that
  // shift the boundary.
  const plan: SourcedSpan[] = [];
  const firstDay = localDayInZone(tz, new Date(from - DAY_MS));
  let cursor = zonedTimeToUtc(tz, firstDay.ymd, 0).getTime();
  const guard = to + 2 * DAY_MS;
  const seen = new Set<string>();
  while (cursor < guard) {
    const local = localDayInZone(tz, new Date(cursor));
    if (seen.has(local.ymd)) {
      cursor += DAY_MS;
      continue;
    }
    seen.add(local.ymd);

    const dayPlanId = assignment.weekly[local.day];
    const dayPlan = dayPlanId ? dayPlanById.get(dayPlanId) : undefined;
    if (dayPlan) {
      for (const block of dayPlan.blocks) {
        const blockPlaylistId = playlistIdOfBlock(block);
        if (!blockPlaylistId || !playlistById.has(blockPlaylistId)) continue;
        const startMin = minutesOfDay(block.start);
        const endMin = minutesOfDay(block.end);
        if (endMin <= startMin) continue;
        const s = zonedTimeToUtc(tz, local.ymd, startMin).getTime();
        const e = zonedTimeToUtc(tz, local.ymd, endMin).getTime();
        // Clipped to the resolve WINDOW - this is "how much of the block this
        // response covers", not "when the block truly began". `s` itself
        // (unclipped) rides through as `loopStart` below: a block resolved on
        // its 200th poll must anchor `playheadAt` exactly like its 1st, or the
        // loop's elapsed time resets to ~0 on every poll and never advances
        // past whatever fits inside one poll interval. See CONTENT-29.
        const clippedStart = Math.max(from, s);
        const clippedEnd = Math.min(to, e);
        if (clippedEnd > clippedStart) {
          plan.push({
            start: clippedStart,
            end: clippedEnd,
            source: "plan",
            playlistId: blockPlaylistId,
            loopStart: s,
          });
        }
      }
    }
    cursor += DAY_MS;
  }

  const fallback: SourcedSpan[] = [];
  if (
    assignment.defaultPlaylistId &&
    playlistById.has(assignment.defaultPlaylistId)
  ) {
    fallback.push({
      start: from,
      end: to,
      source: "default",
      playlistId: assignment.defaultPlaylistId,
      // No HH:mm boundary of its own to anchor to - this layer is "whenever
      // nothing else claims the screen", not a scheduled block. Anchoring at
      // `from` would reproduce the exact same every-poll reset as the plan
      // layer had, just with no block to blame it on, so it gets the same
      // stable-across-polls treatment: today's local midnight. That still
      // resets once a day rather than never, but a day is nothing like a 30s
      // poll interval.
      loopStart: zonedTimeToUtc(
        tz,
        localDayInZone(tz, new Date(from)).ymd,
        0
      ).getTime(),
    });
  }

  const merged = layerByPrecedence(override, plan, fallback);

  const asIntervals = (spans: SourcedSpan[]): SignageContentInterval[] =>
    spans.map((s) => ({
      startsAt: new Date(s.start).toISOString(),
      endsAt: new Date(s.end).toISOString(),
      loopStartsAt: new Date(s.loopStart).toISOString(),
      source: s.source,
      playlistId: s.playlistId,
    }));

  const content: SignageContentInterval[] = asIntervals(merged);

  /*
   * What the WEEK says, with no takeover applied.
   *
   * `content` is what the player plays, so it is cut around the override and
   * must stay that way. But a CMS strip showing the cut version draws the
   * schedule with a hole punched in it, and a hole is not what an operator's
   * week looks like - they want to see the plan continuing underneath, quiet,
   * while the takeover sits on top of it.
   *
   * Only emitted when an override actually masks something. Absent means "the
   * schedule IS the content", and a caller that copied `content` in that case
   * would be shipping the same array twice on every poll.
   */
  const scheduledContent = override.length
    ? asIntervals(layerByPrecedence([], plan, fallback))
    : undefined;

  /* -- campaign breaks --------------------------------------------------- */

  const breaks = resolveBreaks(input, tz, from, to);

  /* -- referenced playlists --------------------------------------------- */

  const usedIds = new Set(content.map((c) => c.playlistId));
  const playlists = [...usedIds]
    .map((id) => playlistById.get(id))
    .filter((p): p is SignagePlaylist => Boolean(p))
    // Catalogue titles become plain `assetUrl` items HERE, so the player needs
    // no knowledge of them at all (`CONTENT-13`).
    .map((playlist) =>
      materialiseContent(playlist, input.contentUrls, warnings)
    );

  /* -- referenced template instances ------------------------------------ */

  // Gathered from the emitted playlists AND breaks: a campaign can show a
  // slide too, and collecting from `playlists` alone would leave a break item
  // with nothing to draw.
  const templates = collectTemplates(
    [...playlists.flatMap((p) => p.items), ...breaks.flatMap((b) => b.items)],
    input.templates,
    warnings
  );

  return {
    screenId: assignment.screenId,
    generatedAt: new Date(from).toISOString(),
    horizonEndsAt: new Date(to).toISOString(),
    timezone: tz,
    content,
    // Same omit-when-absent rule as `templates` and `warnings` below: a panel
    // that never heard of this field sees exactly the document it saw before.
    ...(scheduledContent ? { scheduledContent } : {}),
    breaks,
    playlists,
    // Omitted when empty so an older panel sees exactly the document it saw
    // before this field existed.
    ...(templates.length ? { templates } : {}),
    soundEnabled: assignment.soundEnabled,
    revision: revisionOf(content, breaks, playlists, assignment, templates),
    // Omitted rather than empty: a warnings key that is always present invites
    // `timeline.warnings.length` checks that read as "healthy" when the field
    // is simply an empty array on an old cached payload.
    ...(warnings.length ? { warnings } : {}),
  };
}

/**
 * Place campaign breaks across the window.
 *
 * Markers are spaced evenly through each clock hour, so `breaksPerHour: 4`
 * fires at :00, :15, :30 and :45. The portal instead derives signage markers
 * from the fixed ad-marker grid, which couples campaign timing to ad
 * inventory - with ads out of scope that coupling has no reason to exist.
 */
function resolveBreaks(
  input: ResolveSignageInput,
  tz: string,
  from: number,
  to: number
): SignageBreak[] {
  const { assignment } = input;
  const perHour = Math.floor(assignment.breaksPerHour);
  if (perHour <= 0) return [];
  if (assignment.campaignIds.length === 0) return [];

  const eligible = input.campaigns.filter(
    (c) => assignment.campaignIds.includes(c.id) && c.enabled
  );
  if (eligible.length === 0) return [];

  const spacing = Math.floor(60 / Math.min(perHour, 60));
  const out: SignageBreak[] = [];

  const firstHour = Math.floor(from / HOUR_MS) * HOUR_MS;
  for (let hour = firstHour; hour < to; hour += HOUR_MS) {
    for (let i = 0; i < perHour; i += 1) {
      const at = hour + i * spacing * MINUTE_MS;
      if (at < from || at >= to) continue;

      const local = localDayInZone(tz, new Date(at));
      const localMinutes = localMinutesOf(tz, new Date(at));

      // Re-filter from the FULL eligible list at every marker. The portal
      // narrows its accumulator in place inside the hour loop, so a campaign
      // that is out of window early in the horizon can never come back later
      // in it even when its own rule says it should.
      const live = eligible.filter((c) =>
        campaignCovers(c, local.ymd, local.day, localMinutes)
      );
      if (live.length === 0) continue;

      // The break's OWN wall-clock marker decides where it starts scanning
      // the rotation - see `fillBreak` for why this replaced a bucket
      // carried between calls.
      const slot = Math.round(at / (spacing * MINUTE_MS));
      const picked = fillBreak(live, slot, assignment.breakSeconds);
      if (picked.items.length === 0) continue;

      out.push({
        startsAt: new Date(at).toISOString(),
        durationSeconds: assignment.breakSeconds,
        items: picked.items,
        campaignIds: picked.campaignIds,
      });
    }
  }
  return out;
}

/**
 * Turn catalogue items into plain URL items, dropping what cannot be played.
 *
 * ## Why a DROP rather than a failure
 *
 * A title that was placeable when it was authored can stop being so: the
 * catalogue is not ours, and its resources change under us. Failing the whole
 * timeline would take a lobby dark over one re-packaged film, so the item goes
 * and the loop plays on — but it goes LOUDLY, because a wall quietly playing
 * four items instead of five is the kind of thing nobody notices for a month.
 *
 * ## Why the item keeps its own duration and fit
 *
 * They are properties of the PLACEMENT, not of the title. An operator who said
 * "show this for 20 seconds" means it whether the file behind it is a house
 * clip or a studio's film.
 */
/**
 * Every template instance these items show, resolved once each.
 *
 * Deduplicated: a slide used twelve times in a loop is carried once and the
 * items reference it by id, which is the same indirection `playlists` uses on
 * the timeline and for the same reason - a design with twenty regions repeated
 * per item would dominate the payload.
 *
 * An instance the caller could not resolve (deleted template, deleted instance)
 * is reported as a warning and its items simply draw nothing. Failing the whole
 * timeline instead would take a lobby dark over one deleted slide, which is the
 * stance `content_unavailable` already takes for a re-packaged catalogue title.
 */
function collectTemplates(
  items: SignageItem[],
  resolved: Record<string, ResolvedSignageTemplate | null> | undefined,
  warnings: SignageWarning[]
): ResolvedSignageTemplate[] {
  const wanted = [
    ...new Set(
      items
        .map((i) => i.templateInstanceId)
        .filter((id): id is string => typeof id === "string" && id !== "")
    ),
  ];
  if (wanted.length === 0) return [];

  const out: ResolvedSignageTemplate[] = [];
  for (const id of wanted) {
    const hit = resolved?.[id];
    if (hit) {
      out.push(hit);
      continue;
    }
    warnings.push({
      code: "template_unavailable",
      value: id,
      message:
        "A slide in this schedule no longer exists, so it shows nothing. Remove it from the playlist, or rebuild it from its template.",
    });
  }
  return out;
}

function materialiseContent(
  playlist: SignagePlaylist,
  contentUrls: Record<string, ResolvedContentSource | null> | undefined,
  warnings: SignageWarning[]
): SignagePlaylist {
  if (!playlist.items.some((item) => item.contentAssetId)) return playlist;

  const items: SignageItem[] = [];
  // A live channel's own catalogue artwork, the first one found among this
  // playlist's items - a FALLBACK for a playlist that never had its own
  // `effect`/`logoUrl` (a channel scheduled straight onto a day, `BE-41`;
  // see `channelBlockPlaylist`, which never sets either). Never overrides a
  // value a stored "effect channel" playlist already carries from its own
  // CMS-set `logoUrl` - that one may deliberately differ from the raw
  // catalogue mark, and resolving fresh here must not clobber a curated
  // choice with a guess.
  let channelLogoUrl: string | null = null;
  for (const item of playlist.items) {
    if (!item.contentAssetId) {
      items.push(item);
      continue;
    }
    const source = contentUrls?.[item.contentAssetId] ?? null;
    if (!source) {
      warnings.push({
        code: "content_unavailable",
        value: item.contentAssetId,
        // No longer "made encrypted": protected titles play (PLAYER-6). What
        // reaches this branch is a title with nothing playable AT ALL - or a
        // protected one carrying no DASH rendition and no DRM content id, which
        // is a re-packaging problem and the only encryption case still refused.
        message: `A catalogue title in "${playlist.name}" has no playable file right now, so it was left out of the loop. It may have been withdrawn, or re-packaged into something with no rendition this player can license.`,
      });
      continue;
    }
    if (source.live && source.logoUrl && !channelLogoUrl) {
      channelLogoUrl = source.logoUrl;
    }
    // The asset's own facts travel WITH the url. The caller has already
    // fetched it to get that url; dropping what else it knew is what left
    // three downstream readers guessing.
    items.push({
      ...item,
      assetUrl: source.url,
      live: source.live,
      protocol: source.protocol,
      // CONTENT-26: a TITLE's own catalogue mark, for the bottom-right
      // supplier overlay. Gated on `!live` because a live channel's mark is
      // already the effect channel's `logoUrl` bottom-left (above), and the
      // same image in two corners of one picture is not attribution.
      ...(!source.live && source.logoUrl
        ? { supplierLogoUrl: source.logoUrl }
        : {}),
    });
  }
  return {
    ...playlist,
    items,
    ...(channelLogoUrl && !playlist.logoUrl
      ? { effect: true, logoUrl: channelLogoUrl }
      : {}),
  };
}

/** Minutes since local midnight for an instant, in `tz`. */
export function localMinutesOf(tz: string, instant: Date): number {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  });
  const p = Object.fromEntries(
    fmt.formatToParts(instant).map((x) => [x.type, x.value])
  ) as Record<string, string>;
  const hour = p.hour === "24" ? 0 : Number(p.hour);
  return hour * 60 + Number(p.minute);
}

/**
 * Fill one break by walking the rotation order once, starting from `slot`,
 * without exceeding the break's length or repeating a campaign inside it.
 *
 * ## Why a slot, not a carried bucket
 *
 * The previous version threaded a queue between calls: each break consumed
 * entries from it, and an empty queue refilled from {@link rotationOrder}.
 * Correct in theory, useless in practice - this resolver reruns from
 * scratch on every poll (the panel and the CMS preview both re-resolve the
 * whole horizon roughly every 30s, with no memory of what an earlier
 * resolution already handed out), so almost every break an operator
 * actually watches is the FIRST one drawn in its own resolution, from a
 * queue nothing has ever taken a turn out of. A campaign whose one item
 * fills the entire break (so it can never share a slot with anything else)
 * was then decided purely by whichever candidate the greedy scan reached
 * first in a FRESH queue - never by priority, and never by an actual turn
 * having passed. Found 2026-09-02: a `high`-priority campaign that could not
 * coexist with a smaller `normal` one never played at all, because the
 * normal one always sorted earlier and always fit first.
 *
 * `slot` is a plain function of the break's own wall-clock marker (see the
 * caller), so the same break instant always resolves to the same pick
 * however many times, or how rarely, it gets re-resolved - and DIFFERENT
 * break instants walk to different rotation positions, which is what makes
 * this genuinely round-robin instead of "whichever the scan meets first".
 * Two same-priority campaigns that can never share a break (their items
 * fill it alone) alternate one-for-one across consecutive slots; a `high`
 * one still gets roughly twice the turns of a `normal` one, because it
 * still occupies two positions in {@link rotationOrder} - it is just which
 * position a given break starts scanning FROM that changed.
 */
function fillBreak(
  live: SignageCampaign[],
  slot: number,
  seconds: number
): { items: SignageItem[]; campaignIds: string[] } {
  const order = rotationOrder(live);
  const items: SignageItem[] = [];
  const campaignIds: string[] = [];
  if (order.length === 0) return { items, campaignIds };

  let used = 0;
  const start = ((slot % order.length) + order.length) % order.length;
  // One full lap of the rotation, starting from this break's own position -
  // packs as many DISTINCT campaigns as still fit, same as the pod ever did.
  for (let step = 0; step < order.length; step += 1) {
    const c = order[(start + step) % order.length]!;
    if (campaignIds.includes(c.id)) continue;
    const cost = campaignSeconds(c);
    if (cost <= 0 || used + cost > seconds) continue;
    items.push(...c.items);
    campaignIds.push(c.id);
    used += cost;
    if (used >= seconds) break;
  }

  return { items, campaignIds };
}

function campaignSeconds(c: SignageCampaign): number {
  return c.items.reduce((sum, i) => sum + Math.max(0, i.durationSeconds), 0);
}

/* -------------------------------------------------------------------------- */
/* Revision                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * A cheap content hash over everything a player actually renders.
 *
 * Deliberately includes item durations, mute flags and sound - the portal's
 * equivalent hashes only asset ids, so changing a slide's duration produces an
 * identical id and the change never reaches the screen.
 *
 * ⚠️ **"Everything a player renders" means EVERY field, and we grew the exact
 * defect we refused to carry.** Until this was fixed the item hash covered
 * `assetUrl`, duration, fit and mute - but not `kind`, and not
 * `contentAssetId`. A timeline is cached for six hours and the revision is the
 * only change signal a panel has, so swapping an item's kind, or repointing it
 * at a different catalogue title, produced an identical revision and never
 * reached the wall. It was invisible while there were two kinds and one way to
 * name a file; adding `url` (and, next, template instances, whose whole
 * identity is a field this hash did not read) would have made it a silent
 * "my edit does nothing" bug on a lobby screen.
 *
 * **The rule: a field an operator can edit that changes a pixel MUST appear
 * here.** Anything new on `SignageItem` is added to this line in the same
 * commit that adds it to the type.
 */
function revisionOf(
  content: SignageContentInterval[],
  breaks: SignageBreak[],
  playlists: SignagePlaylist[],
  assignment: SignageAssignment,
  templates: ResolvedSignageTemplate[]
): string {
  const parts = [
    ...content.map(
      (c) => `${c.startsAt}|${c.endsAt}|${c.source}|${c.playlistId}`
    ),
    ...breaks.map(
      (b) => `${b.startsAt}|${b.durationSeconds}|${b.campaignIds.join(",")}`
    ),
    ...playlists.map(
      (p) =>
        `${p.id}|${p.soundEnabled}|${p.items
          .map(
            (i) =>
              `${i.kind}:${i.assetUrl}:${i.webUrl ?? ""}:${
                i.contentAssetId ?? ""
              }:${i.templateInstanceId ?? ""}:${i.durationSeconds}:${i.fit}:${
                i.muted
              }`
          )
          .join(",")}`
    ),
    /*
     * A slide changes when its VALUES change or when its DESIGN changes, and
     * both have to land here.
     *
     * `version` covers the design without hashing every region: the template
     * store bumps it on save, so a designer moving a logo produces a new
     * revision on every wall showing any slide built from it. Hashing the
     * regions themselves would work too and would cost a walk of the whole
     * design on every poll, for the same answer.
     *
     * The values are sorted by key, because `Object.entries` order follows
     * insertion and a re-saved instance with the same values in a different
     * order would otherwise look like a change to every panel in the estate.
     */
    ...templates.map(
      (t) =>
        `tpl:${t.instanceId}:${t.templateId}:${t.version}:${Object.keys(
          t.values
        )
          .sort()
          .map((k) => `${k}=${t.values[k]}`)
          .join("|")}`
    ),
    `sound=${assignment.soundEnabled}`,
    `orientation=${assignment.orientation}`,
  ];
  return fnv1a(parts.join("\n"));
}

/**
 * FNV-1a, 32-bit, hex. Not a security primitive - a change detector.
 *
 * Exported (not just used by `revisionOf` above) so `legacySignageSchedule.ts`
 * can hash its own content-addressed playlist ids with something that runs in
 * a browser - `node:crypto`'s `createHash` doesn't exist client-side, which
 * matters once this adapter is called in-process from the TV app itself
 * rather than only served over HTTP to a legacy device (see the
 * "surgical port" plan). 32-bit collision risk is negligible at the scale
 * this keys (a handful of playlists per screen's schedule, not millions).
 */
export function fnv1a(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

/* -------------------------------------------------------------------------- */
/* Playhead                                                                    */
/* -------------------------------------------------------------------------- */

export interface SignagePlayhead {
  interval: SignageContentInterval;
  playlist: SignagePlaylist;
  /** The item on screen, or null when the playlist has no items. */
  item: SignageItem | null;
  /** Zero-based position of `item` within the playlist. */
  index: number;
  /** Seconds elapsed into `item`. */
  elapsedSeconds: number;
  /** How long `item` holds the screen. */
  itemSeconds: number;
  /** Total loop length of the playlist, in seconds. */
  loopSeconds: number;
}

/** An item's effective length: its own, or the playlist's default when unset. */
export function itemSeconds(
  item: SignageItem,
  playlist: SignagePlaylist
): number {
  return item.durationSeconds > 0
    ? item.durationSeconds
    : playlist.defaultItemSeconds;
}

/**
 * What is on screen at `now`, and how far into it we are.
 *
 * Derived by assuming the playlist starts from its first item when the
 * interval begins and loops cleanly. That is exact for images and for videos
 * with a declared duration. For a video set to "play to its natural end"
 * (`durationSeconds: 0`) the real length is only known to the player, so the
 * playlist's default is substituted and the position becomes an ESTIMATE -
 * good enough to render a progress bar in the CMS, and never used to drive
 * playback.
 */
export function playheadAt(
  timeline: SignageTimeline,
  now: Date
): SignagePlayhead | null {
  const t = now.getTime();
  const interval =
    timeline.content.find(
      (c) => Date.parse(c.startsAt) <= t && t < Date.parse(c.endsAt)
    ) ??
    // Nothing contains us. If we are only a little BEFORE the window - a clock
    // that trails the server's - take the first interval rather than going
    // black. See CLOCK_SKEW_GRACE_MS.
    timeline.content.find((c) => {
      const start = Date.parse(c.startsAt);
      return t < start && start - t <= CLOCK_SKEW_GRACE_MS;
    });
  if (!interval) return null;

  const playlist = timeline.playlists.find((p) => p.id === interval.playlistId);
  if (!playlist) return null;

  const items = playlist.items;
  if (items.length === 0) {
    return {
      interval,
      playlist,
      item: null,
      index: 0,
      elapsedSeconds: 0,
      itemSeconds: 0,
      loopSeconds: 0,
    };
  }

  const durations = items.map((i) => itemSeconds(i, playlist));
  const loopSeconds = durations.reduce((a, b) => a + b, 0);
  if (loopSeconds <= 0) {
    return {
      interval,
      playlist,
      item: items[0]!,
      index: 0,
      elapsedSeconds: 0,
      itemSeconds: 0,
      loopSeconds: 0,
    };
  }

  // Anchored on `loopStartsAt`, NOT `startsAt`. `startsAt` is clipped to the
  // resolve window (`Math.max(from, s)` in `resolveSignageTimeline`) and is
  // near-`now` on every poll after the first, which used to reset this
  // offset to ~0 every ~30s regardless of how long the block had truly been
  // running - pinning the estimate near item 0 forever on any loop longer
  // than one poll interval. `loopStartsAt` is the block/override's real,
  // unclamped origin and does not move between polls. See CONTENT-29.
  //
  // Still clamped at 0, because the skew tolerance above can hand back an
  // interval that has not started yet. A panel a little early is at the
  // START of the loop - a negative offset would index backwards through the
  // playlist.
  //
  // Falls back to `startsAt` when `loopStartsAt` is missing or unparseable
  // (an older cached timeline, or a hand-built fixture) - `||`, not `??`,
  // because `Date.parse` of an absent/malformed field is `NaN`, which is
  // falsy but not nullish.
  const anchor =
    Date.parse(interval.loopStartsAt) || Date.parse(interval.startsAt);
  const since = Math.max(0, t - anchor);
  let offset = Math.floor(since / 1000) % loopSeconds;
  let index = 0;
  while (index < durations.length && offset >= durations[index]!) {
    offset -= durations[index]!;
    index += 1;
  }
  if (index >= items.length) index = items.length - 1;

  return {
    interval,
    playlist,
    item: items[index]!,
    index,
    elapsedSeconds: offset,
    itemSeconds: durations[index]!,
    loopSeconds,
  };
}

/* -------------------------------------------------------------------------- */
/* Breaks - what interrupts the loop, and when it must not                     */
/* -------------------------------------------------------------------------- */

/** What a break is showing at an instant. */
export interface SignageBreakPlayhead {
  brk: SignageBreak;
  item: SignageItem;
  index: number;
  /** Seconds into this item. */
  elapsedSeconds: number;
  itemSeconds: number;
}

/**
 * May a break interrupt whatever is on screen right now?
 *
 * ⛔ **This fails CLOSED, and that is the whole point.** The rule it enforces is
 * "a linear channel is never interrupted", which is a rights question and not a
 * preference: we do not own a broadcaster's airtime and cannot sell it.
 *
 * The reference implementation gets this backwards. `nenda-autoplayer`'s guard
 * ends `if (!currentContentPlaylist) return true;` - so mid-schedule-update, or
 * on a playlist lookup miss, ads run over anything. Its permission is also
 * data, set by an operator toggle with no awareness that a channel is a
 * channel, and the marker grid never consults it at all.
 *
 * ## Why this is currently wider than the rule
 *
 * Three cases are refused, and only the first two are the rule itself:
 *
 *  1. **A channel BLOCK** - the playlist id carries the marker, so this is
 *     exact.
 *  2. **Nothing resolvable on screen** - a break over a black wall is a lone
 *     advertisement in an empty room, and "I cannot tell" must never resolve to
 *     "go ahead".
 *  3. **A live channel ITEM inside a mixed playlist**, which the resolver now
 *     marks (`SignageItem.live`) from the asset's own type.
 *
 * ⚠️ Case 3 used to refuse over ANY item carrying a `contentAssetId`,
 * deliberately over-broad, because a live channel and a VOD were the same
 * shape and the failure mode of a wrong guess is an advertisement over live
 * broadcast. That stand-in stopped being cheap the moment venue content
 * arrived: effect channels are catalogue VODs, and they are precisely what a
 * wall plays all day - so the wide guard would have switched the ad product
 * off on the content it exists to run under, silently. The resolver knows;
 * it no longer guesses.
 */
export function breakMayInterrupt(
  timeline: SignageTimeline,
  now: Date
): boolean {
  const head = playheadAt(timeline, now);
  if (!head) return false;
  if (isChannelPlaylistId(head.playlist.id)) return false;
  if (!head.item) return false;
  // THE RULE, exactly. Not "any catalogue item" - that was the stand-in while
  // a live channel and a VOD were indistinguishable, and it would have
  // switched ads off on venue content, which is what a signage wall plays all
  // day. The resolver now says which is which.
  if (head.item.live) return false;
  return true;
}

/**
 * What a break is showing at `now`, or null when the loop should have the wall.
 *
 * Clock-derived, exactly like {@link playheadAt}, and that is a deliberate
 * departure from the reference implementation. `nenda-autoplayer` walks an
 * index through the pod and has to bank the interrupted item's remaining
 * duration with a 1 s rewind (`calculatePlaybackTimes`) so content can resume
 * mid-asset. Foyer's playhead is a pure function of the clock and holds no
 * position, so there is nothing to bank: when the break ends, `playheadAt`
 * answers wherever the wall clock now points, which IS resuming mid-item. No
 * state, no rewind constant, no drift between what a panel believes and what
 * the CMS believes it believes.
 *
 * A pod whose items run shorter than the nominal `durationSeconds` returns null
 * for the remainder rather than holding a last frame - the loop takes the wall
 * back early, which is what the reference does when its pod completes.
 */
export function breakAt(
  timeline: SignageTimeline,
  now: Date
): SignageBreakPlayhead | null {
  const t = now.getTime();
  const brk = timeline.breaks.find((b) => {
    const start = Date.parse(b.startsAt);
    if (Number.isNaN(start)) return false;
    return start <= t && t < start + Math.max(0, b.durationSeconds) * 1000;
  });
  if (!brk) return null;

  // Asked AFTER a break is found, so the cost is paid only at a marker rather
  // than on every one of the panel's 1 Hz ticks.
  if (!breakMayInterrupt(timeline, now)) return null;

  let offset = Math.floor((t - Date.parse(brk.startsAt)) / 1000);
  for (let index = 0; index < brk.items.length; index += 1) {
    const item = brk.items[index]!;
    // `fillBreak` only ever picks campaigns whose items sum above zero, so this
    // is a guard against a hand-made timeline rather than the normal path. A
    // zero-length item takes no time and is simply never on screen.
    const seconds = Math.max(0, item.durationSeconds);
    if (seconds === 0) continue;
    if (offset < seconds) {
      return { brk, item, index, elapsedSeconds: offset, itemSeconds: seconds };
    }
    offset -= seconds;
  }
  return null;
}

/**
 * Seconds until the next campaign break MARKER strictly after `now`, or
 * `null` when the horizon holds none (no eligible campaigns, `breaksPerHour:
 * 0`, or the resolved window has simply run out).
 *
 * Deliberately answers only "when is the next one", never "is one running
 * right now" - that is {@link breakAt}'s question, and a caller who wants
 * "how long do I wait" rather than a marker that doubles as the one already
 * playing should check `!breakAt(timeline, now)` first, as
 * `SignageScreenOverview.tsx` does. This exists because that question used to
 * have no answer at all: breaks fire for `breakSeconds` out of every `3600 /
 * breaksPerHour`, commonly a ~7% duty cycle, so glancing at a screen between
 * markers looks identical to a campaign that is not running - and was
 * mistaken for exactly that on 2026-09-02.
 */
export function nextBreakInSeconds(
  timeline: SignageTimeline,
  now: Date
): number | null {
  const t = now.getTime();
  let soonest: number | null = null;
  for (const b of timeline.breaks) {
    const start = Date.parse(b.startsAt);
    if (Number.isNaN(start) || start <= t) continue;
    if (soonest === null || start < soonest) soonest = start;
  }
  return soonest === null ? null : Math.round((soonest - t) / 1000);
}

/** `m:ss`, for the progress readout under the now-playing item. */
export function formatClock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  return `${m.toString().padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;
}

/** "1 h 18 min" / "45 min" - the loop-length readout beside a playlist name. */
export function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  // ⚠️ Under a minute this used to round to "0 min", so a real 15-second loop
  // read as having no length at all - which looks exactly like a playlist
  // whose durations were never set. Signage loops ARE this short: a two-item
  // lobby rotation is thirty seconds.
  if (s < 60) return `${s} s`;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
}

export const __testing = { subtract, rotationOrder, fillBreak, DAY_KEYS };
